import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, Clock, User, ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { siteContent } from '../data/mockData';

const ArticlePage = ({ articleId, onBack }) => {
  const articles = siteContent.en.blogPosts;
  const article = articles.find(post => post.id === articleId);

  if (!article) {
    return (
      <div className="py-20 lg:py-32 bg-white min-h-screen">
        <SEOHead 
          title="Article Not Found | MedQuore Insights"
          description="The requested article could not be found. Browse our other medical device quality and compliance insights."
          keywords="medquore insights, medical device articles, article not found"
          canonical="https://medquore.com/insights"
        />
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-[#011431] mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">The requested article could not be found.</p>
            <button
              onClick={onBack}
              className="inline-flex items-center text-[#c41927] hover:text-[#a01520] font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Insights
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Mock full article content
  const getFullContent = (id) => {
    const contents = {
      "1": {
        sections: [
          {
            title: "Introduction",
            content: "ISO 13485:2016 represents a significant evolution in quality management systems for medical devices. This comprehensive guide will walk you through the essential steps for a successful migration while avoiding the common pitfalls that can derail your implementation."
          },
          {
            title: "Understanding the Key Changes",
            content: "The 2016 revision introduced several critical changes including enhanced risk management integration, strengthened supplier controls, and improved post-market surveillance requirements. These changes reflect the evolving regulatory landscape and the increasing focus on patient safety."
          },
          {
            title: "Strategic Migration Approach",
            content: "A successful migration requires a systematic approach starting with gap analysis, followed by risk assessment, implementation planning, and thorough validation. Each phase must be carefully managed to ensure regulatory compliance and operational efficiency."
          },
          {
            title: "Common Pitfalls and How to Avoid Them",
            content: "Many organizations fall into predictable traps during migration: inadequate gap analysis, insufficient training, poor change management, and rushed implementation timelines. Our experience shows that proper planning and expert guidance can eliminate 95% of these issues."
          },
          {
            title: "Implementation Best Practices",
            content: "Key success factors include executive commitment, cross-functional team formation, adequate resource allocation, and phased implementation. Companies that follow these practices typically achieve certification 40% faster than those that don't."
          },
          {
            title: "Conclusion",
            content: "ISO 13485:2016 migration is a strategic investment in your organization's future. With proper planning and execution, it becomes a competitive advantage that enhances quality, reduces risk, and accelerates market access."
          }
        ]
      },
      "2": {
        sections: [
          {
            title: "The MDR Journey: Two Years Later",
            content: "The European Union's Medical Device Regulation (MDR) has been in full effect for over two years, providing valuable insights into real-world implementation challenges and success strategies."
          },
          {
            title: "Key Lessons Learned",
            content: "Early adopters have demonstrated that proactive preparation, robust clinical evidence, and strong post-market surveillance systems are critical for MDR success. Companies that started early have seen significant competitive advantages."
          },
          {
            title: "Technical Documentation Excellence",
            content: "MDR demands unprecedented levels of technical documentation quality. Organizations must invest in systematic document management, clinical evaluation expertise, and regulatory intelligence capabilities."
          },
          {
            title: "Post-Market Surveillance Evolution",
            content: "The MDR's enhanced post-market surveillance requirements have transformed how companies monitor device performance and manage clinical risks throughout the product lifecycle."
          },
          {
            title: "Future Outlook",
            content: "As the regulatory landscape continues to evolve, companies that have successfully navigated MDR implementation are well-positioned for future regulatory changes and market opportunities."
          }
        ]
      },
      "3": {
        sections: [
          {
            title: "Documentation as a Strategic Asset",
            content: "In the medical device industry, documentation is not just a regulatory requirement—it's a strategic asset that can differentiate your organization during audits and inspections."
          },
          {
            title: "The Audit-Ready Framework",
            content: "Building audit-ready documentation requires systematic thinking, standardized templates, version control, and regular review processes. This framework ensures consistency and compliance across all quality management activities."
          },
          {
            title: "Digital Transformation in QMS",
            content: "Modern eQMS platforms enable automated workflows, real-time tracking, and integrated risk management. Organizations leveraging these technologies report 60% faster audit preparation times."
          },
          {
            title: "Best Practices for Implementation",
            content: "Success requires clear ownership, regular training, and continuous improvement processes. Companies that embed these practices into their culture achieve superior audit outcomes and operational efficiency."
          }
        ]
      }
    };
    return contents[id] || { sections: [] };
  };

  const fullContent = getFullContent(article.id);

  return (
    <div className="py-20 lg:py-32 bg-white min-h-screen">
      <SEOHead 
        title={`${article.title} | MedQuore Insights`}
        description={article.excerpt}
        keywords={`${article.category.toLowerCase()}, medical device ${article.category.toLowerCase()}, ${article.title.toLowerCase()}, medquore insights`}
        canonical={`https://medquore.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.image}
      />
      <div className="container mx-auto px-4 lg:px-6 max-w-4xl">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="inline-flex items-center text-[#c41927] hover:text-[#a01520] font-medium mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Insights
        </button>

        {/* Article Header */}
        <div className="mb-12">
          <div className="flex items-center mb-4">
            <Badge variant="secondary" className="bg-[#c41927] text-white mr-4">
              {article.category}
            </Badge>
            <div className="flex items-center text-sm text-gray-500 space-x-4">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {article.readTime}
              </div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {article.author}
              </div>
              <span>{formatDate(article.date)}</span>
            </div>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-[#011431] mb-6 leading-tight">
            {article.title}
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed">
            {article.excerpt}
          </p>
        </div>

        {/* Featured Image */}
        <Card className="mb-12 overflow-hidden">
          <div className="h-64 relative overflow-hidden">
            <img 
              src={article.image} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
        </Card>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          {fullContent.sections.map((section, index) => (
            <Card key={index} className="mb-8 shadow-sm border-l-4 border-l-[#c41927]">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#011431] mb-4">
                  {section.title}
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {section.content}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Author Bio */}
        <Card className="mt-12 bg-[#f0f0e8] border-0">
          <CardContent className="p-8">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#011431] to-[#c41927] rounded-full flex items-center justify-center mr-6">
                <span className="text-white font-bold text-lg">
                  {article.author.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#011431] mb-1">{article.author}</h3>
                <p className="text-gray-600">
                  Senior Quality & Regulatory Expert at MedQuore with 15+ years experience in medical device compliance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Related Articles */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-[#011431] mb-8">Related Articles</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {articles.filter(post => post.id !== article.id).slice(0, 2).map((relatedArticle) => (
              <Card key={relatedArticle.id} className="group hover:shadow-lg transition-shadow duration-300 cursor-pointer border-0 shadow-md">
                <div className="h-32 relative overflow-hidden">
                  <img 
                    src={relatedArticle.image} 
                    alt={relatedArticle.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <Badge variant="secondary" className="bg-[#f0f0e8] text-[#011431] text-xs mb-3">
                    {relatedArticle.category}
                  </Badge>
                  <h4 className="font-bold text-[#011431] mb-2 group-hover:text-[#c41927] transition-colors">
                    {relatedArticle.title}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {relatedArticle.excerpt}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;